import { useCallback } from 'react';
import type { StoreSummaryDto } from '@bhops/shared';
import { storesApi } from '@/api/stores';
import { EntityPicker } from './EntityPicker';

// Store search-as-you-type. Typically scoped to a single customer
// (`customerId` prop) so the dropdown surfaces only that
// customer's branches; pass `customerId: null` to allow
// cross-customer search (rare but useful for admin tooling).
//
// When `customerId` changes, the EntityPicker's queryKey changes
// too, which evicts the prior customer's results from the cache.

interface StorePickerProps {
  value: StoreSummaryDto | null;
  onChange: (store: StoreSummaryDto | null) => void;

  /**
   * Filter stores to a single customer. When `null`, the picker
   * searches all stores visible to the caller. Disabling the
   * picker with `disabled` is a different signal — use it when
   * no customer is selected yet and you want the input greyed
   * out instead of allowing a cross-customer search.
   */
  customerId: string | null;

  /**
   * Round 2 Phase F (#8) — true 면 "배정 가능"(계약 완료 ~ 설치 완료) 고객사의
   * 매장만 검색한다. 고객사 없는 매장(이벤트/사내)은 제외된다. 기본 false.
   * 배치 모달(DeployRobotModal)의 "배정 가능 고객사만" 토글에서 전달.
   */
  assignableOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}

export function StorePicker({
  value,
  onChange,
  customerId,
  assignableOnly = false,
  disabled,
  // round5 #8 — 서버 검색이 고객사 상호까지 매칭하므로 기준을 명시.
  placeholder = '상호, 매장명, 지점으로 검색…',
  'data-testid': testId,
}: StorePickerProps) {
  const fetchOptions = useCallback(
    async (query: string): Promise<StoreSummaryDto[]> => {
      const resp = await storesApi.list({
        customerId: customerId ?? undefined,
        search: query,
        pageSize: 20,
        ...(assignableOnly ? { assignableOnly: true } : {}),
      });
      return resp.items;
    },
    [customerId, assignableOnly],
  );

  return (
    <EntityPicker<StoreSummaryDto>
      // Cache key includes the customerId + assignableOnly so changing
      // either doesn't leak prior results into the new dropdown.
      key={customerId ?? '_all_'}
      value={value}
      onChange={onChange}
      fetchOptions={fetchOptions}
      queryKey={`store-picker:${customerId ?? '_all_'}:${assignableOnly ? 'assignable' : 'all'}`}
      getId={(s) => s.id}
      // round5 #8 — '매장이라는 기준이 뭔지 모르겠다' 해소: 상호를 라벨에 병기.
      getLabel={(s) => {
        const storePart = s.branchName ? `${s.name} · ${s.branchName}` : s.name;
        const customerName = s.customer?.displayName;
        return customerName && customerName !== s.name
          ? `${customerName} — ${storePart}`
          : storePart;
      }}
      getSubLabel={(s) => [s.city, s.region, s.country].filter((x): x is string => !!x).join(' · ')}
      disabled={disabled}
      placeholder={placeholder}
      data-testid={testId ?? 'store-picker'}
      emptyMessage={
        assignableOnly
          ? "일치하는 매장이 없습니다. '배정 가능 고객사만' 필터가 켜져 있으니, 끄고 다시 검색해 보세요."
          : '일치하는 매장이 없습니다.'
      }
    />
  );
}
